import React, { useContext, useEffect, useState, useMemo } from 'react';
import * as actionType from '../context/actions';
import CustomerTableDataLayer from './CustomerTableDataLayer';
import { ConfigContext } from '../context/GlobalContext';
import { useNavigate } from 'react-router-dom';
import BusinessDropdown from './child/BusinessDropdown';
import { Languages } from '../Utils/Languages';
import { Icon } from '@iconify/react';

const CustomerListLayer = () => {
    const { state, dispatch } = useContext(ConfigContext);
    const navigate = useNavigate();

    const [selectedBusiness, setSelectedBusiness] = useState("null");
    const [searchText, setSearchText] = useState("");

    // ===========================
    // PLAN CONFIG
    // ===========================
    const starterPlanName = process.env.REACT_APP_STARTER_PLAN_NAME;
    const professionalPlanName = process.env.REACT_APP_PROFESSIONAL_PLAN_NAME;
    const agencyPlanName = process.env.REACT_APP_AGENCY_PLAN_NAME;

    const userPlanName = state?.PAYMENT?.[state?.PAYMENT?.length - 1]?.planName || "Free Plan";

    const isFreePlan = !state?.PREMIUM;
    const isStarterPlan = userPlanName === starterPlanName;
    const isProfessionalPlan = userPlanName === professionalPlanName;
    const isAgencyPlan = userPlanName === agencyPlanName;

    // ===========================
    // SORT BUSINESSES (Oldest → Newest)
    // ===========================
    const sortedBusinesses = useMemo(() => {
        return [...(state?.BUSINESSES || [])]
            .sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
    }, [state?.BUSINESSES]);

    // ===========================
    // BUSINESS ACCESS LIMIT
    // ===========================
    let allowedBusinessCount = 1; // Free & Starter

    if (isProfessionalPlan) {
        allowedBusinessCount = 3;
    }

    if (isAgencyPlan) {
        allowedBusinessCount = sortedBusinesses.length;
    }

    const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
    const sharedBusinesses = sortedBusinesses.filter(b => b.isShared);

    const allowedOwnIds = ownBusinesses
        .slice(-allowedBusinessCount)
        .map(b => String(b.companyId));
    const sharedIds = sharedBusinesses.map(b => String(b.companyId));

    const allAllowedIds = [...allowedOwnIds, ...sharedIds];

    // ===========================
    // SYNC SELECTED BUSINESS
    // ===========================
    useEffect(() => {
        if (!state?.BUSINESSES?.length) return;

        if (state?.SELECTEDBUSINESS === "null") {
            setSelectedBusiness("null");
            return;
        }

        const selected =
            state?.BUSINESSES?.find(b => b?.companyId == state?.SELECTEDBUSINESS)?.companyId ||
            "null";

        setSelectedBusiness(selected);
    }, [state?.BUSINESSES, state?.SELECTEDBUSINESS]);

    const availableCurrencies = useMemo(() => {
        const list = new Set();
        const bizFilter = selectedBusiness !== "null" ? [String(selectedBusiness)] : allAllowedIds;

        (state?.INVOICES || []).forEach(inv => {
            if (inv.currency && bizFilter.includes(String(inv.companyId))) {
                list.add(inv.currency);
            }
        });
        (state?.CUSTOMERS || []).forEach(cust => {
            if (cust.currency && bizFilter.includes(String(cust.companyId))) {
                list.add(cust.currency);
            }
        });
        return Array.from(list);
    }, [state?.INVOICES, state?.CUSTOMERS, selectedBusiness, allAllowedIds]);

    // ===========================
    // SYNC CURRENCY FILTER (Default to first available, or active business currency)
    // ===========================
    useEffect(() => {
        if (availableCurrencies.length > 0) {
            if (!state?.CUSTOMER_CURRENCY_FILTER || !availableCurrencies.includes(state.CUSTOMER_CURRENCY_FILTER)) {
                dispatch({ type: actionType.CUSTOMER_CURRENCY_FILTER, payload: availableCurrencies[0] });
            }
        } else {
            const fallback = state?.CURRENCYCODE || "INR";
            if (!state?.CUSTOMER_CURRENCY_FILTER || state?.CUSTOMER_CURRENCY_FILTER === "All") {
                dispatch({ type: actionType.CUSTOMER_CURRENCY_FILTER, payload: fallback });
            }
        }
    }, [availableCurrencies, state?.CUSTOMER_CURRENCY_FILTER, state?.CURRENCYCODE]);

    const handleSelection = (businessId) => {
        setSelectedBusiness(businessId);
        dispatch({ type: actionType.SELECTEDBUSINESS, payload: businessId });
    };

    return (
        <div className="card basic-data-table">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                <BusinessDropdown
                    businesses={sortedBusinesses}
                    selectedCompanyId={state?.SELECTEDBUSINESS}
                    onBusinessChange={handleSelection}
                    onPricingNavigate={() => navigate("/pricing")}
                    showAllOption={true}
                    isAllowedCheck={(business) => {
                        const ownBusinesses = sortedBusinesses.filter(b => !b.isShared);
                        const allowedOwnIds = ownBusinesses
                            .slice(-allowedBusinessCount)
                            .map(b => b.companyId);

                        return (
                            business.isShared ||
                            isAgencyPlan ||
                            allowedOwnIds.includes(business.companyId)
                        );
                    }}
                />

                <div className="d-flex align-items-center gap-3">
                    {/* Currency filter dropdown */}
                    {availableCurrencies.length > 1 && (
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-primary-600 dropdown-toggle px-16 py-8 radius-8 d-flex align-items-center gap-2"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                style={{ minWidth: "160px" }}
                            >
                                <Icon icon="lucide:coins" className="text-lg" />
                                <span>
                                    {state?.CUSTOMER_CURRENCY_FILTER || state?.CURRENCYCODE || "INR"}
                                </span>
                            </button>
                            <ul className="dropdown-menu shadow-md border-0 radius-8 p-8" style={{ zIndex: 1050 }}>
                                {availableCurrencies.map(curr => (
                                    <li key={curr} className="mt-1">
                                        <button
                                            className={`dropdown-item px-16 py-8 rounded ${state?.CUSTOMER_CURRENCY_FILTER === curr ? "bg-primary text-white" : "text-secondary-light bg-hover-neutral-200"}`}
                                            onClick={() => dispatch({ type: actionType.CUSTOMER_CURRENCY_FILTER, payload: curr })}
                                        >
                                            {curr}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Search input in header */}
                    <div className="d-flex align-items-center position-relative">
                        <Icon
                            icon="ion:search-outline"
                            className="position-absolute text-secondary-light"
                            style={{ left: 10, fontSize: 16, pointerEvents: "none" }}
                        />
                        <input
                            type="text"
                            className="form-control"
                            style={{ paddingLeft: 32, minWidth: 220 }}
                            placeholder={Languages[state?.LANGUAGE]?.search_customer || "Search customers..."}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <CustomerTableDataLayer
                businessData={state?.BUSINESSES}
                selectedBusiness={selectedBusiness}
                allAllowedIds={allAllowedIds}
                searchText={searchText}
            />
        </div>
    );
};

export default CustomerListLayer;